/**
 * components/AppShell — the authenticated layout: top bar (live equity + kill switch),
 * the nav rail / bottom bar, and the routed page area. It owns the single shell-wide
 * real-time feed ({@link useLive}: SSE with poll fallback) and hands the snapshot down
 * to pages via the router Outlet context, so pages read one live source without each
 * opening their own connection.
 */
import { Outlet, useOutletContext } from 'react-router-dom';

import { useLive } from '../api/useLive';
import type { LiveState } from '../api/useLive';
import { useAuth } from '../auth/AuthProvider';
import { HaltBanner } from './HaltBanner';
import { NavBottom, NavRail } from './Nav';
import { TopBar } from './TopBar';

export type ShellContext = LiveState;

export function AppShell(): JSX.Element {
  const { logout } = useAuth();
  const live = useLive({ onUnauthorized: logout });

  const ov = live.overview;
  const equity = ov?.equity ?? live.positions?.equity ?? null;
  const dailyPct = ov?.dailyPnlPct ?? null;
  const halted = ov?.halt.halted ?? false;
  const demo = ov?.demo ?? live.positions?.source === 'demo-seed';

  return (
    <div className="shell">
      <TopBar
        equity={equity}
        dailyPct={dailyPct}
        halted={halted}
        demo={Boolean(demo)}
        transport={live.transport}
      />
      <div className="shell__body">
        <aside className="shell__rail">
          <NavRail />
        </aside>
        <main className="shell__main">
          {halted && <HaltBanner reason={ov?.halt.reason ?? null} />}
          <Outlet context={live} />
        </main>
      </div>
      <NavBottom />
    </div>
  );
}

/** Typed accessor for pages rendered inside the shell's Outlet. */
export function useShell(): ShellContext {
  return useOutletContext<ShellContext>();
}
